import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import ReactPlayer from "react-player";

import projects from "../../data/projects";
import Gallery from "../../components/gallery/Gallery";

// ---------------------------------------------------------
// Animation variants
// ---------------------------------------------------------

const fadeUp = {
  hidden: {
    opacity: 0,
    y: 40,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const fadeLeft = {
  hidden: {
    opacity: 0,
    x: -50,
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.8,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const fadeRight = {
  hidden: {
    opacity: 0,
    x: 50,
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.8,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const ProjectDetails = () => {
  const { slug } = useParams();

  const project = projects.find((item) => item.slug === slug);

  const [expanded, setExpanded] = useState(false);

  // ---------------------------------------------------------
  // Project not found
  // ---------------------------------------------------------

  if (!project) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="section--spacing-x flex min-h-[70vh] flex-col items-center justify-center py-32 text-center"
      >
        <h1 className="heading--xxxl">Project Not Found</h1>

        <Link
          to="/projects"
          className="mt-8 rounded-full bg-black px-8 py-3 text-white transition duration-300 hover:bg-neutral-700"
        >
          Back to Projects
        </Link>
      </motion.div>
    );
  }

  // ---------------------------------------------------------
  // Description
  // ---------------------------------------------------------

  const description = project.description || "";

  const maxChars = 500;

  const isLong = description.length > maxChars;

  const shortText = isLong
    ? `${description.slice(0, maxChars)}...`
    : description;

  // ---------------------------------------------------------
  // Highlight labels
  // ---------------------------------------------------------

  const highlightLabels = {
    projectType: "Project",
    numberOfApartments: "Number of apartments",
    sizeOfApartments: "Size of apartments",
    numberOfCarParkings: "Number of car parkings",
    rajukApprovalNo: "RAJUK approval no",
    landArea: "Land area",
    architecturalFirm: "Architectural Firm",
    facing: "Facing",
    frontRoad: "Front Road",
  };

  // ---------------------------------------------------------
  // Highlights
  // ---------------------------------------------------------

  const highlights = Object.entries(project.highlights || {}).filter(
    ([_, value]) => value !== null && value !== "",
  );

  // ---------------------------------------------------------
  // Video fallback image
  // ---------------------------------------------------------

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{
        opacity: 0,
        transition: {
          duration: 0.3,
        },
      }}
      className="dark:bg-slate-600"
    >
      {/* =====================================================
          COVER IMAGE
      ====================================================== */}

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{
          duration: 1,
          ease: "easeOut",
        }}
        className="group relative overflow-hidden"
      >
        <motion.img
          initial={{
            scale: 1.08,
          }}
          animate={{
            scale: 1,
          }}
          transition={{
            duration: 1.4,
            ease: [0.22, 1, 0.36, 1],
          }}
          className="h-64 w-full object-cover lg:h-[550px]"
          src={project.cover}
          alt={project.title}
        />

        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black/40" />
      </motion.div>

      {/* =====================================================
          PROJECT DESCRIPTION
      ====================================================== */}

      <motion.section
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        viewport={{
          once: true,
          amount: 0.15,
        }}
        className="section--spacing-x mt-20 grid grid-cols-1 gap-8 text-primary lg:mx-20 xl:grid-cols-3"
      >
        {/* Left */}
        <motion.div
          variants={fadeLeft}
          className="flex flex-col justify-start font-light"
        >
          <h4 className="text-darkOrange font-oswald text-xl uppercase">
            {project.developer}
          </h4>

          <h2 className="heading--xxxl text-secondary">{project.title}</h2>

          <h4 className="font-oswald text-xl">{project.location}</h4>
        </motion.div>

        {/* Right */}
        <motion.div
          variants={fadeRight}
          className="description--styles xl:col-span-2"
        >
          {/* Desktop */}
          <p className="font-helvetica hidden text-justify text-xl font-light leading-relaxed md:block">
            {description}
          </p>

          {/* Mobile */}
          <div className="block md:hidden">
            <p className="font-helvetica text-justify text-base font-light leading-relaxed">
              {expanded ? description : shortText}
            </p>

            {isLong && (
              <button
                type="button"
                onClick={() => setExpanded((previous) => !previous)}
                className="font-helvetica text-darkOrange mt-3 text-sm transition-opacity duration-300 hover:opacity-70 focus:outline-none"
              >
                {expanded ? "See less" : "See more"}
              </button>
            )}
          </div>
        </motion.div>
      </motion.section>

      {/* =====================================================
          PROJECT HIGHLIGHTS
      ====================================================== */}

      <motion.section
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        viewport={{
          once: true,
          amount: 0.15,
        }}
        className="section--spacing-x section--spacing-y grid grid-cols-1 gap-10 font-oswald lg:grid-cols-3"
      >
        {/* Left - Highlights */}
        <motion.div
          variants={fadeLeft}
          className="flex flex-col items-start justify-start gap-5"
        >
          <h2 className="text-greyText text-2xl font-light uppercase">
            Project Highlights
          </h2>

          <div className="lg:mt-8">
            {highlights.map(([key, value]) => (
              <p
                key={key}
                className="text-xl font-light leading-relaxed lg:font-normal"
              >
                <span className="font-light">
                  {highlightLabels[key] || key.replace(/([A-Z])/g, " $1")} :{" "}
                </span>

                <span className="text-greyText font-thin">{value}</span>
              </p>
            ))}
          </div>
        </motion.div>

        {/* Right - Video */}
        <motion.div variants={fadeRight} className="md:col-span-2">
          <div className="relative aspect-video overflow-hidden md:aspect-21/9 lg:aspect-video xl:aspect-21/9">
            {project.videoUrl ? (
              <ReactPlayer
                url={project.videoUrl}
                width="100%"
                height="100%"
                controls
                muted={false}
                playsInline
              />
            ) : (
              <img
                src={project.cover}
                alt={`${project.title} preview`}
                className="absolute left-0 top-0 h-full w-full object-cover"
              />
            )}
          </div>
        </motion.div>
      </motion.section>

      {/* =====================================================
          PROJECT GALLERY
      ====================================================== */}

      {project.images?.length > 0 && (
        <motion.section
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{
            once: true,
            amount: 0.1,
          }}
          className="mx-3 mb-20 mt-36 md:mx-0 md:ps-4 xl:ps-10"
        >
          <Gallery images={project.images} />
        </motion.section>
      )}

      {/* =====================================================
          BROCHURE + MAP
      ====================================================== */}

      {/* <motion.section
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        viewport={{
          once: true,
          amount: 0.1,
        }}
        className="grid grid-cols-1 gap-2 mt-20 mb-32 bg-neutral-300 md:grid-cols-3"
      >
        <motion.div
          variants={fadeLeft}
          className="flex items-center py-6 tracking-wider text-white uppercase section--spacing-x heading--one"
        >
          {project.brochure ? (
            <a
              href={project.brochure}
              download
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 transition duration-300 bg-transparent rounded text-secondary hover:bg-gray-200"
            >
              <svg
                className="w-4 h-4 mr-2 fill-current"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
              >
                <path d="M13 8V2H7v6H2l8 8 8-8h-5zM0 18h20v2H0v-2z" />
              </svg>

              <span>Brochure</span>
            </a>
          ) : null}
        </motion.div>

        <motion.div variants={fadeRight} className="md:col-span-2">
          {project.mapUrl ? (
            <div className="relative overflow-hidden aspect-21/9">
              <iframe
                src={project.mapUrl}
                width="100%"
                height="100%"
                className="absolute inset-0"
                frameBorder="0"
                title={`${project.title} location map`}
                marginHeight="0"
                marginWidth="0"
                scrolling="no"
                loading="lazy"
              />
            </div>
          ) : null}
        </motion.div>
      </motion.section> */}

      {/* =====================================================
          BACK TO PROJECTS
      ====================================================== */}

      {/* <motion.div
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        viewport={{
          once: true,
          amount: 0.5,
        }}
        className="flex justify-center mb-20"
      >
        <Link
          to="/projects"
          className="px-8 py-3 text-white transition duration-300 bg-black rounded-full hover:bg-neutral-700"
        >
          Back to Projects
        </Link>
      </motion.div> */}
    </motion.div>
  );
};

export default ProjectDetails;
